import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';

import {
  listIntegrationErrors,
  resolveIntegrationError,
  type ListIntegrationErrorsQuery,
} from '@/api/cs/operations.api';
import { CsErrorCard, CsLoading, SELECT_CLS } from '@/components/cs/PageStates';
import { describeCsError } from '@/components/cs/csError';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';

/**
 * Operations → Errors (spec §3.10.5). Every failed outbound CS call
 * (Chatwoot, Anthropic, …) lands in this feed. Filters: provider,
 * window, unresolved-only; rows resolve manually (retry belongs to the
 * producing vertical, not the log).
 */

const PROVIDERS = ['chatwoot', 'anthropic'] as const;

export function ErrorsTab() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [provider, setProvider] = useState('');
  const [sinceHours, setSinceHours] = useState('');
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [page, setPage] = useState(1);

  const query: ListIntegrationErrorsQuery = {
    ...(provider !== '' ? { provider } : {}),
    ...(sinceHours !== '' ? { sinceHours: Number(sinceHours) } : {}),
    unresolvedOnly,
    page,
  };

  const errorsQuery = useQuery({
    queryKey: ['cs', 'integration-errors', query],
    queryFn: () => listIntegrationErrors(query),
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => resolveIntegrationError(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cs', 'integration-errors'] });
      toast.show('Marked resolved.', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not resolve the error.'), { variant: 'danger' });
    },
  });

  if (errorsQuery.isLoading) return <CsLoading />;
  if (errorsQuery.error) {
    return (
      <CsErrorCard title="Couldn't load the error log" error={errorsQuery.error} fallback="Unknown error" />
    );
  }

  const rows = errorsQuery.data?.data ?? [];
  const meta = errorsQuery.data?.meta ?? { page: 1, limit: 25, total: 0 };
  const pageCount = Math.max(1, Math.ceil(meta.total / meta.limit));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Provider filter"
          value={provider}
          onChange={(e) => {
            setProvider(e.target.value);
            setPage(1);
          }}
          className={SELECT_CLS}
        >
          <option value="">All providers</option>
          {PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select
          aria-label="Window filter"
          value={sinceHours}
          onChange={(e) => {
            setSinceHours(e.target.value);
            setPage(1);
          }}
          className={SELECT_CLS}
        >
          <option value="">All time</option>
          <option value="24">Last 24h</option>
          <option value="168">Last 7d</option>
          <option value="720">Last 30d</option>
        </select>
        <label className="flex items-center gap-2 text-[13px] text-kw-shark">
          <input
            type="checkbox"
            checked={unresolvedOnly}
            onChange={(e) => {
              setUnresolvedOnly(e.target.checked);
              setPage(1);
            }}
          />
          Unresolved only
        </label>
        <span className="ml-auto text-[12px] text-kw-muted">{String(meta.total)} error(s)</span>
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[760px] text-left">
          <thead>
            <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
              <th className="px-4 py-2.5 font-medium">When</th>
              <th className="px-4 py-2.5 font-medium">Provider</th>
              <th className="px-4 py-2.5 font-medium">Endpoint</th>
              <th className="px-4 py-2.5 font-medium">Message</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-kw-border">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[13px] text-kw-muted">
                  No integration errors match — quiet is good.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="text-[13px] text-kw-shark">
                  <td className="whitespace-nowrap px-4 py-2.5 text-kw-muted">
                    {new Date(row.occurredAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5">{row.provider}</td>
                  <td className="px-4 py-2.5 font-mono text-[12px]">{row.endpoint}</td>
                  <td className="max-w-72 px-4 py-2.5">
                    <p className="truncate" title={row.message}>
                      {row.message}
                    </p>
                    {row.httpStatus !== null ? (
                      <p className="text-[11px] text-kw-muted">HTTP {row.httpStatus}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5">
                    {row.resolvedAt ? (
                      <Badge variant="success">Resolved</Badge>
                    ) : row.retryable ? (
                      <Badge variant="warning">Retryable</Badge>
                    ) : (
                      <Badge variant="danger">Open</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {!row.resolvedAt ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={resolveMutation.isPending}
                        onClick={() => {
                          resolveMutation.mutate(row.id);
                        }}
                      >
                        <CheckCircle2 className="size-4" />
                        Resolve
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {meta.total > meta.limit ? (
        <div className="flex items-center justify-between text-[13px] text-kw-muted">
          <span>
            Page {String(meta.page)} of {String(pageCount)}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
              }}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= pageCount}
              onClick={() => {
                setPage((p) => p + 1);
              }}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
